import { z } from "zod";
import type { Middleware } from "../app";

export interface ValidatorSchema {
	parse: (data: unknown) => unknown;
}

export interface ValidateOptions {
	body?: ValidatorSchema;
	params?: ValidatorSchema;
}

/** @deprecated Prefer `zValidator()`, which gives you `ctx.valid()` with full type inference. */
export const validate = (schemas: ValidateOptions): Middleware => {
	return async (ctx, next) => {
		const errors: string[] = [];

		if (schemas.body) {
			try {
				const bodyData = await ctx.body();
				ctx.store.validatedBody = schemas.body.parse(bodyData);
			} catch (err: unknown) {
				const message = err instanceof Error ? err.message : "Invalid body";
				errors.push(`body: ${message}`);
			}
		}

		if (schemas.params) {
			try {
				ctx.store.validatedParams = schemas.params.parse(ctx.params);
			} catch (err: unknown) {
				const message = err instanceof Error ? err.message : "Invalid params";
				errors.push(`params: ${message}`);
			}
		}

		if (errors.length > 0) {
			return ctx.json(
				{
					error: "Validation Failed",
					details: errors,
				},
				400,
			);
		}

		return next();
	};
};

/** @deprecated Prefer `zValidator("body", schema)`. */
export const validateBody = (schema: ValidatorSchema): Middleware => {
	return validate({ body: schema });
};

/** @deprecated Prefer `zValidator("params", schema)`. */
export const validateParams = (schema: ValidatorSchema): Middleware => {
	return validate({ params: schema });
};

export type ValidationTarget = "body" | "query" | "params";

/**
 * Content types supported by `zValidator("body", schema, { contentType })`.
 * Defaults to `application/json` when `contentType` is omitted.
 */
export type BodyContentType =
	/**
	 * Standard JSON body - default behavior when `contentType` is omitted.
	 *
	 * Parsed via `JSON.parse`. Zod schema receives the parsed JavaScript object.
	 *
	 * @example
	 * zValidator("body", z.object({ name: z.string() }))
	 * // equivalent to: { contentType: "application/json" }
	 */
	| "application/json"
	/**
	 * Parse fields from `multipart/form-data`.
	 * Supports both text fields (as strings) and file fields (as File objects).
	 * Validate file fields using `z.file()` (requires Zod v4+).
	 *
	 * Zod schema receives `Record<string, string | File>`. of all non-file fields.
	 *
	 * @example
	 * zValidator("body", z.object({ name: z.string() }), { contentType: "multipart/form-data" })
	 */
	| "multipart/form-data"
	/**
	 * Parse fields from an `application/x-www-form-urlencoded` body.
	 * All values are strings - use `z.coerce.number()`, `z.coerce.boolean()`, etc. for coercion.
	 *
	 * Zod schema receives `Record<string, string>`.
	 *
	 * @example
	 * zValidator("body", z.object({ email: z.string().email(), age: z.coerce.number() }), {
	 *   contentType: "application/x-www-form-urlencoded",
	 * })
	 */
	| "application/x-www-form-urlencoded"
	/**
	 * Parse a plain text body.
	 * Zod schema receives the raw body as a `string`.
	 *
	 * @example
	 * zValidator("body", z.string().min(1), { contentType: "text/plain" })
	 */
	| "text/plain"
	/**
	 * Parse an XML body (W3C standard MIME type).
	 * Zod schema receives the raw XML as a `string` - parse it further with your preferred XML library.
	 *
	 * @example
	 * zValidator("body", z.string(), { contentType: "application/xml" })
	 */
	| "application/xml"
	/**
	 * Parse an XML body (legacy/vendor MIME type, functionally identical to `application/xml`).
	 * Zod schema receives the raw XML as a `string`.
	 *
	 * @example
	 * zValidator("body", z.string(), { contentType: "text/xml" })
	 */
	| "text/xml"
	/**
	 * Parse a raw binary body.
	 * Zod schema receives an `ArrayBuffer` - use `z.instanceof(ArrayBuffer)` or process it manually.
	 *
	 * @example
	 * zValidator("body", z.instanceof(ArrayBuffer), { contentType: "application/octet-stream" })
	 */
	| "application/octet-stream";

export interface ZValidatorOptions {
	/**
	 * Override the expected request body content type.
	 * Only applies when `target` is `"body"`. Defaults to `application/json`.
	 *
	 * - `"multipart/form-data"` - text and file fields from FormData; validate files with `z.file()`
	 * - `"application/x-www-form-urlencoded"` - URL-encoded key=value body
	 *
	 * Omit this option (or the entire `options` argument) for standard JSON bodies.
	 */
	contentType?: BodyContentType;
}

export type SchemaType = z.ZodType | Record<string, z.ZodTypeAny>;

function wrapSchema(schema: SchemaType): z.ZodType {
	// biome-ignore lint/suspicious/noExplicitAny: Required to check internal Zod fields
	return schema instanceof z.ZodType || (schema as any)?._def
		? (schema as z.ZodType)
		: z.object(schema as Record<string, z.ZodTypeAny>);
}

/**
 * Validate request body/query/params against a Zod schema and expose the
 * parsed, typed result via `ctx.valid(target)` - no manual casting needed.
 *
 * ```ts
 * const schema = z.object({ name: z.string(), age: z.number() });
 *
 * // JSON body (default)
 * app.post("/users", zValidator("body", schema), (ctx) => {
 *   const data = ctx.valid<z.infer<typeof schema>>("body");
 *   return ctx.json(data);
 * });
 *
 * // multipart/form-data text fields (pair with uploader() for files)
 * app.post("/profile", zValidator("body", schema, { contentType: "multipart/form-data" }), ...);
 *
 * // application/x-www-form-urlencoded
 * app.post("/login", zValidator("body", schema, { contentType: "application/x-www-form-urlencoded" }), ...);
 * ```
 *
 * On failure, responds with 422 and structured error details.
 */
export function zValidator(
	target: ValidationTarget,
	schema: SchemaType | (new () => unknown),
	options?: ZValidatorOptions,
): Middleware {
	const finalSchema = wrapSchema(schema as SchemaType);
	const resolvedContentType: BodyContentType =
		options?.contentType ?? "application/json";

	const middleware: Middleware = async (ctx, next) => {
		let raw: unknown;

		if (target === "body") {
			if (resolvedContentType === "multipart/form-data") {
				// Parse both text and file fields from multipart/form-data
				const ct = ctx.request.headers.get("content-type") || "";
				if (!ct.includes("multipart/form-data")) {
					return ctx.error("Validation Failed", 422, [
						{ field: "content-type", message: "expected multipart/form-data content-type" },
					]);
				}
				try {
					const formData = await ctx.formData();
					const fields: Record<string, string | File> = {};
					for (const [key, value] of formData.entries()) {
						if (value instanceof File) {
							fields[key] = value;
						} else {
							fields[key] = value.toString();
						}
					}
					raw = fields;
			} catch {
				return ctx.error("Validation Failed", 422, [
					{ field: "body", message: "failed to parse multipart/form-data" },
				]);
				}
			} else if (resolvedContentType === "application/x-www-form-urlencoded") {
				const ct = ctx.request.headers.get("content-type") || "";
			if (!ct.includes("application/x-www-form-urlencoded")) {
				return ctx.error("Validation Failed", 422, [
					{ field: "content-type", message: "expected application/x-www-form-urlencoded content-type" },
				]);
				}
				try {
					const text = await ctx.request.text();
					const params = new URLSearchParams(text);
					const fields: Record<string, string> = {};
					for (const [key, value] of params.entries()) {
						fields[key] = value;
					}
					raw = fields;
			} catch {
				return ctx.error("Validation Failed", 422, [
					{ field: "body", message: "failed to parse application/x-www-form-urlencoded" },
				]);
				}
			} else if (
				resolvedContentType === "text/plain" ||
				resolvedContentType === "application/xml" ||
				resolvedContentType === "text/xml"
			) {
				const ct = ctx.request.headers.get("content-type") || "";
			if (!ct.includes(resolvedContentType)) {
				return ctx.error("Validation Failed", 422, [
					{ field: "content-type", message: `expected ${resolvedContentType} content-type` },
				]);
				}
				try {
					raw = await ctx.request.text();
			} catch {
				return ctx.error("Validation Failed", 422, [
					{ field: "body", message: `failed to read ${resolvedContentType} body` },
				]);
				}
			} else if (resolvedContentType === "application/octet-stream") {
				const ct = ctx.request.headers.get("content-type") || "";
			if (!ct.includes("application/octet-stream")) {
				return ctx.error("Validation Failed", 422, [
					{ field: "content-type", message: "expected application/octet-stream content-type" },
				]);
				}
				try {
					raw = await ctx.request.arrayBuffer();
			} catch {
				return ctx.error("Validation Failed", 422, [
					{ field: "body", message: "failed to read application/octet-stream body" },
				]);
				}
			} else {
				// Default: application/json
				try {
					raw = await ctx.body();
			} catch {
				return ctx.error("Validation Failed", 422, [
					{ field: "body", message: "invalid JSON" },
				]);
			}
			}
		} else if (target === "query") {
			raw = ctx.query;
		} else {
			raw = ctx.params;
		}

		const result = finalSchema.safeParse(raw);
		if (!result.success) {
			return ctx.error(
				"Validation Failed",
				422,
				result.error.issues.map(
					(issue) => ({
						field: issue.path.join(".") || target,
						message: issue.message,
					}),
				),
			);
		}

		ctx.setValidated(target, result.data);

		return next();
	};

	// Attach metadata for OpenAPI generation
	const mwMeta = middleware as unknown as Record<string, unknown>;
	mwMeta._isBuntokValidator = true;
	mwMeta._target = target;
	mwMeta._schema = finalSchema;
	mwMeta._contentType = target === "body" ? resolvedContentType : "";

	return middleware;
}

/**
 * Document the response schema for OpenAPI.
 * Note: Currently this is for documentation purposes only and does not perform runtime response validation.
 */
export function zResponse(
	status: number,
	schema: SchemaType | (new () => unknown) | [new () => unknown],
	description: string = "Success",
): Middleware {
	const finalSchema = wrapSchema(schema as SchemaType);

	// Wrap in standard Buntok response envelope
	const envelopedSchema = z.object({
		success: z.boolean(),
		message: z.string(),
		data: finalSchema,
	});

	const middleware: Middleware = async (_ctx, next) => {
		return next();
	};

	// Attach metadata for OpenAPI generation
	const mwMeta = middleware as unknown as Record<string, unknown>;
	mwMeta._isBuntokResponse = true;
	mwMeta._status = status;
	mwMeta._schema = envelopedSchema;
	mwMeta._description = description;

	return middleware;
}
